import React from 'react';
import { Download, RefreshCw, Users, CreditCard, HardDrive, ClipboardList, ShieldAlert, Activity, UserCog, Newspaper, Zap, Crown } from 'lucide-react';

const TABS = [
  { id: 'health', icon: Activity, label: 'Health', shortLabel: 'Health', testId: 'admin-tab-health' },
  { id: 'llm_health', icon: Zap, label: 'LLM Health', shortLabel: 'LLM', testId: 'admin-tab-llm-health' },
  { id: 'storage_health', icon: HardDrive, label: 'Storage Health', shortLabel: 'R2', testId: 'admin-tab-storage-health' },
  { id: 'users', icon: UserCog, label: 'Users', shortLabel: 'Users', testId: 'admin-tab-users' },
  { id: 'ambassadors', icon: Users, label: 'Ambassadors', shortLabel: 'Ambass.', testId: 'admin-tab-ambassadors' },
  { id: 'pioneers', icon: Crown, label: 'Pioneers', shortLabel: 'Pioneers', testId: 'admin-tab-pioneers' },
  { id: 'revenue', icon: CreditCard, label: 'Payments & Subs', shortLabel: 'Revenue', testId: 'admin-tab-revenue' },
  { id: 'storage', icon: HardDrive, label: 'Storage', shortLabel: 'Storage', testId: 'admin-tab-storage' },
  { id: 'content', icon: Newspaper, label: 'Content', shortLabel: 'Content', testId: 'admin-tab-content' },
  { id: 'audit', icon: ClipboardList, label: 'Audit Log', shortLabel: 'Audit', testId: 'admin-tab-audit' },
  { id: 'support', icon: ShieldAlert, label: 'Support', shortLabel: 'Help', testId: 'admin-tab-support' },
  { id: 'addons_revenue', icon: CreditCard, label: 'Add-ons MRR', shortLabel: 'MRR', testId: 'admin-tab-addons-revenue' },
];

const TAB_TITLES = {
  health: 'Platform Health',
  llm_health: 'LLM Health Check',
  storage_health: 'R2 Storage Health',
  users: 'User Management',
  ambassadors: 'Ambassadors',
  pioneers: 'Pioneer Soft-Launch',
  revenue: 'Revenue',
  storage: 'Storage',
  content: 'Content & Spotlight',
  audit: 'Audit Log',
  support: 'Support Tickets',
  addons_revenue: 'Add-ons Revenue',
};

const TAB_DESCRIPTIONS = {
  health: 'Birds-eye view of revenue, engagement, and storage growth.',
  llm_health: 'Probe every LLM model in production. Catches deprecations & rate-limits before users do.',
  storage_health: 'Cloudflare R2 bucket size, garbage-collection sweep status, and pending-cleanup drift.',
  users: 'Add, edit, and manage all platform users and their subscriptions.',
  ambassadors: 'Manage the global ambassador promo (first 1000 verified signups).',
  pioneers: 'Mint Pioneer claim invites for the oldest mobile-waitlist signups. Each invite grants lifetime Pro on claim.',
  revenue: 'View payments, subscriptions, and webhook events.',
  storage: 'Monitor storage usage, compression savings, and duplicate detection.',
  content: 'Curate the WhatsNewBanner changelog and the weekly Featured Creator spotlight.',
  support: 'Manage student support tickets and respond to inquiries.',
  addons_revenue: 'Premium Add-ons MRR, adoption rates, and churn metrics.',
  audit: 'Track admin actions for compliance and audit trail.',
};

export const AdminHeader = ({ tab, userEmail, onRefresh, onDownload }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-b border-[var(--f500-line)] pb-6">
    <div>
      <p className="f500-eyebrow mb-2">— Admin Console</p>
      <h1 className="f500-display text-3xl sm:text-4xl">
        {TAB_TITLES[tab] || 'Admin'}
      </h1>
      <p className="f500-body text-sm mt-2">
        {TAB_DESCRIPTIONS[tab]} <span className="text-[var(--f500-ink-3)]">Signed in as {userEmail}.</span>
      </p>
    </div>
    <div className="flex gap-2 flex-wrap">
      {tab === 'ambassadors' && (
        <button
          type="button"
          onClick={() => onDownload('ambassadors')}
          className="f500-btn f500-btn-primary"
          data-testid="admin-download-csv"
        >
          <Download className="w-4 h-4" /> CSV
        </button>
      )}
      {tab === 'revenue' && (
        <>
          <button type="button" onClick={() => onDownload('payments')} className="f500-btn f500-btn-primary" data-testid="admin-download-payments-csv">
            <Download className="w-4 h-4" /> Payments
          </button>
          <button type="button" onClick={() => onDownload('subscriptions')} className="f500-btn f500-btn-secondary" data-testid="admin-download-subs-csv">
            <Download className="w-4 h-4" /> Subs
          </button>
          <button type="button" onClick={() => onDownload('events')} className="f500-btn f500-btn-secondary" data-testid="admin-download-events-csv">
            <Download className="w-4 h-4" /> Events
          </button>
        </>
      )}
      <button
        type="button"
        onClick={onRefresh}
        className="f500-btn f500-btn-secondary"
      >
        <RefreshCw className="w-4 h-4" /> Refresh
      </button>
    </div>
  </div>
);

export const AdminTabs = ({ tab, onChange }) => (
  <div className="mt-6 flex border border-[var(--f500-line)] bg-[var(--isp-page)] overflow-x-auto">
    {TABS.map(({ id, icon: Icon, label, shortLabel, testId }, i) => {
      const isActive = tab === id;
      return (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          className={`flex-1 min-w-[110px] flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${i > 0 ? 'border-l border-[var(--f500-line)]' : ''} ${
            isActive
              ? 'bg-[var(--f500-ink)] text-white'
              : 'text-[var(--f500-ink-2)] hover:bg-[var(--f500-paper-2)] hover:text-[var(--f500-ink)]'
          }`}
          style={{ fontFamily: 'var(--sf-text)' }}
          data-testid={testId}
        >
          <Icon className="w-4 h-4" strokeWidth={1.75} />
          <span className="hidden sm:inline">{label}</span>
          <span className="sm:hidden">{shortLabel}</span>
        </button>
      );
    })}
  </div>
);
